import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { LOAN_TYPES, inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import {
  ArrowRight, LogIn, Handshake, FileText, CheckCircle2, ShieldCheck,
  Zap, TrendingUp, Building2, Loader2, IndianRupee,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Phone, Mail, MessageCircle,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";

const STAT_ICONS = [Zap, Building2, TrendingUp, ShieldCheck];
const SOCIAL_META = [
  { key: "facebook", icon: Facebook }, { key: "instagram", icon: Instagram },
  { key: "linkedin", icon: Linkedin }, { key: "twitter", icon: Twitter },
  { key: "youtube", icon: Youtube }, { key: "whatsapp", icon: MessageCircle },
];

function TopNav({ company }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3">
          <LogoMark className="w-9 h-9" />
          <div>
            <div className="text-white font-bold font-heading leading-tight text-sm">{company}</div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono">DSA Platform</div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-300">
          <a href="#apply" className="hover:text-white transition-colors">Apply for Loan</a>
          <Link to="/partner-signup" className="hover:text-white transition-colors">Become a Partner</Link>
          <a href="#lenders" className="hover:text-white transition-colors">Lenders</a>
        </nav>
        <Link to="/login" data-testid="home-login-link"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium text-sm transition-all border border-white/10">
          <LogIn size={16} /> Login
        </Link>
      </div>
    </header>
  );
}

function ApplyForm() {
  const [loanTypes, setLoanTypes] = useState(LOAN_TYPES);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", loan_type: LOAN_TYPES[0], loan_amount: "", city: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/public/loan-types").then((r) => setLoanTypes(r.data.loan_types)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/lead", { ...form, loan_amount: parseFloat(form.loan_amount) || 0 });
      setDone(true);
      toast.success("Application submitted! Our team will reach out shortly.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="apply-success" className="text-center py-10">
      <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><CheckCircle2 size={34} /></div>
      <h3 className="text-xl font-bold text-slate-900 font-heading mt-4">Application Received!</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">Thank you. A loan expert will contact you within 24 hours on WhatsApp & call.</p>
      <button onClick={() => { setDone(false); setForm({ customer_name: "", customer_phone: "", customer_email: "", loan_type: loanTypes[0], loan_amount: "", city: "", notes: "" }); }}
        className="mt-5 text-sm font-medium text-emerald-600 hover:text-emerald-700">Submit another application</button>
    </div>
  );

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="apply-form">
      <div className="sm:col-span-2"><label className={labelCls}>Full Name *</label><input data-testid="apply-name-input" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} placeholder="Your name" /></div>
      <div><label className={labelCls}>Phone *</label><input data-testid="apply-phone-input" required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className={inputCls} placeholder="10-digit mobile" /></div>
      <div><label className={labelCls}>Email</label><input data-testid="apply-email-input" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={inputCls} placeholder="you@email.com" /></div>
      <div><label className={labelCls}>Loan Type *</label>
        <select data-testid="apply-loan-type-select" value={form.loan_type} onChange={(e) => setForm({ ...form, loan_type: e.target.value })} className={inputCls}>
          {loanTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div><label className={labelCls}>Loan Amount (₹)</label><input data-testid="apply-amount-input" type="number" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} className={inputCls} placeholder="e.g. 500000" /></div>
      <div className="sm:col-span-2"><label className={labelCls}>City</label><input data-testid="apply-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} placeholder="Your city" /></div>
      <div className="sm:col-span-2"><label className={labelCls}>Message (optional)</label><textarea data-testid="apply-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} placeholder="Tell us about your requirement" /></div>
      <div className="sm:col-span-2">
        <button data-testid="apply-submit-button" type="submit" disabled={loading} className={`${btnPrimary} w-full py-3`}>
          {loading ? <Loader2 size={18} className="animate-spin" /> : <>Submit Application <ArrowRight size={16} /></>}
        </button>
      </div>
    </form>
  );
}

export default function Home() {
  const [c, setC] = useState(null);

  useEffect(() => { api.get("/public/homepage").then((r) => setC(r.data)).catch(() => {}); }, []);

  if (!c) return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-400" size={32} /></div>;

  const lenders = c.lender_list || [];
  const socials = SOCIAL_META.filter((s) => (c.social?.[s.key] || "").trim());

  return (
    <div id="top" className="min-h-screen bg-slate-50">
      <TopNav company={c.footer.company} />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-28 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={c.hero.image} alt="hero" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900/90 to-slate-900/70" />
        </div>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-in">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium border border-emerald-500/20">
              <ShieldCheck size={13} /> {c.hero.badge}
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white font-heading leading-[1.05] tracking-tight">
              {c.hero.title}<br /><span className="text-emerald-400">{c.hero.title_highlight}</span>
            </h1>
            <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-lg leading-relaxed">{c.hero.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#apply" data-testid="hero-apply-cta" className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-sm transition-all shadow-lg shadow-emerald-500/20 active:scale-95">
                <FileText size={17} /> {c.hero.apply_label}
              </a>
              <Link to="/partner-signup" data-testid="hero-partner-cta" className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm transition-all border border-white/15 active:scale-95">
                <Handshake size={17} /> {c.hero.partner_label}
              </Link>
            </div>
          </div>

          <div className="animate-in" style={{ animationDelay: "0.1s" }}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border border-slate-100" id="apply">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><IndianRupee size={20} /></div>
                <h2 className="text-lg font-bold text-slate-900 font-heading">Apply for a Loan</h2>
              </div>
              <p className="text-sm text-slate-500 mb-5">Fill the form and get a callback within 24 hours.</p>
              <ApplyForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {c.stats.map((s, i) => {
            const Icon = STAT_ICONS[i % STAT_ICONS.length];
            return (
              <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center mb-3"><Icon size={20} /></div>
                <div className="text-lg font-extrabold text-slate-900 font-heading">{s.val}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Partner section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center" id="partner">
        <div>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-emerald-300 text-xs font-medium">
            <Handshake size={13} /> {c.partner.badge}
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-slate-900 font-heading tracking-tight">{c.partner.heading}</h2>
          <p className="mt-4 text-slate-600 text-base leading-relaxed max-w-lg">{c.partner.text}</p>
          <ul className="mt-6 space-y-3">
            {c.partner.benefits.map((t, i) => (
              <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><Handshake size={20} /></div>
            <h3 className="text-lg font-bold text-slate-900 font-heading">Partner Registration</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5">Complete your KYC and upload documents to get onboarded.</p>
          <div className="space-y-2 mb-6">
            {c.partner.documents.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600"><FileText size={15} className="text-emerald-500" /> {d}</div>
            ))}
          </div>
          <Link to="/partner-signup" data-testid="partner-register-cta" className={`${btnPrimary} w-full py-3`}>
            Start Partner Registration <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Lenders */}
      {lenders.length > 0 && (
        <section className="bg-slate-900 py-16" id="lenders">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-heading">{c.lenders.heading}</h2>
            <p className="text-slate-400 mt-2 text-sm">{c.lenders.subtext}</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {lenders.map((l) => (
                <div key={l.name} className="flex items-center justify-center gap-2 px-4 py-5 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 text-sm font-semibold">
                  <Building2 size={16} className="text-emerald-400" /> {l.name}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact bar */}
      {(c.contact?.phone || c.contact?.email || c.contact?.helpdesk || c.contact?.address) && (
        <section className="bg-emerald-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white text-sm font-medium">
            {c.contact.phone && <a href={`tel:${c.contact.phone}`} className="flex items-center gap-2 hover:opacity-80"><Phone size={15} /> {c.contact.phone}</a>}
            {c.contact.helpdesk && <span className="flex items-center gap-2"><MessageCircle size={15} /> Helpdesk: {c.contact.helpdesk}</span>}
            {c.contact.email && <a href={`mailto:${c.contact.email}`} className="flex items-center gap-2 hover:opacity-80"><Mail size={15} /> {c.contact.email}</a>}
            {c.contact.address && <span className="flex items-center gap-2"><Building2 size={15} /> {c.contact.address}</span>}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-950 border-t border-slate-800 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark className="w-8 h-8" />
            <div className="text-white font-bold font-heading text-sm">{c.footer.company}</div>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            {c.footer.links.map((lnk, i) => (
              lnk.href?.startsWith("#")
                ? <a key={i} href={lnk.href} className="hover:text-white transition-colors">{lnk.label}</a>
                : <a key={i} href={lnk.href} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">{lnk.label}</a>
            ))}
            <Link to="/login" className="hover:text-white transition-colors">Agent Login</Link>
          </div>
          {socials.length > 0 && (
            <div className="flex items-center gap-3">
              {socials.map((s) => {
                const Icon = s.icon;
                return <a key={s.key} href={c.social[s.key]} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-emerald-600 text-slate-300 hover:text-white flex items-center justify-center transition-colors" data-testid={`footer-social-${s.key}`}><Icon size={15} /></a>;
              })}
            </div>
          )}
          <div className="text-slate-600 text-xs font-mono">{c.footer.copyright}</div>
        </div>
      </footer>
    </div>
  );
}
